import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Calendar, Clock, MapPin, Phone, Mail, ArrowRight, Home, 
  Car, Users, DollarSign, Copy, ExternalLink, Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../services/api';
import LiveRideTracker from '../components/rides/LiveRideTracker';

const RideConfirmation = () => {
  const { rideId } = useParams();
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('ref');
  
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLiveTracker, setShowLiveTracker] = useState(false);

  useEffect(() => {
    const fetchRide = async () => {
      setLoading(true);
      try {
        if (rideId && rideId !== 'test') {
          // Try to fetch ride details - but if it fails due to auth, show basic view
          try {
            const response = await api.get(`/rides/${rideId}`);
            setRide(response.data);
          } catch (fetchError) {
            // If fetch fails (401 or 404), show basic confirmation with reference
            console.log('Could not fetch ride details, showing basic view');
            if (reference) {
              setRide({
                reference_number: reference,
                status: 'requested',
                isBasicView: true
              });
            }
          }
        } else if (reference) {
          // If we only have reference, show a simplified confirmation
          setRide({
            reference_number: reference,
            status: 'requested',
            isBasicView: true
          });
        }
      } catch (err) {
        console.error('Error fetching ride:', err);
        setError('Could not load ride details');
        // Still show basic confirmation if we have reference
        if (reference) {
          setRide({
            reference_number: reference,
            status: 'requested',
            isBasicView: true
          });
        }
      }
      setLoading(false);
    };

    fetchRide();
  }, [rideId, reference]);

  const copyReference = () => {
    const ref = ride?.reference_number || reference;
    navigator.clipboard.writeText(ref);
    toast.success('Reference number copied!');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'ASAP';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#8B5CF6] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 font-['Cormorant_Garamond']">
            Ride Booked Successfully!
          </h1>
          <p className="text-gray-400">
            Your ride request has been received. We'll notify you when a driver is assigned.
          </p>
        </motion.div>

        {/* Reference Number Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-[#8B5CF6]/20 to-[#6D28D9]/20 border border-[#8B5CF6]/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A78BFA] text-sm font-medium mb-1">Ride Reference Number</p>
              <p className="text-3xl font-bold text-white font-mono">
                {ride?.reference_number || reference || 'N/A'}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="icon"
              onClick={copyReference}
              className="border-[#8B5CF6]/50 text-[#A78BFA] hover:bg-[#8B5CF6]/20"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-gray-400 text-sm mt-4">
            Save this reference number for your records. You can use it to track your ride.
          </p>
        </motion.div>

        {/* Ride Details (if available) */}
        {ride && !ride.isBasicView && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-6 space-y-4"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Trip Details</h2>
            
            {/* Pickup */}
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Pickup</p>
                <p className="text-white font-medium">
                  {ride.pickup_location?.address}, {ride.pickup_location?.city}
                </p>
              </div>
            </div>

            {/* Dropoff */}
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Dropoff</p>
                <p className="text-white font-medium">
                  {ride.dropoff_location?.address}, {ride.dropoff_location?.city}
                </p>
              </div>
            </div>

            {/* DateTime */}
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-[#8B5CF6]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-[#8B5CF6]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Pickup Time</p>
                <p className="text-white font-medium">
                  {ride.is_asap ? 'As Soon As Possible' : formatDateTime(ride.pickup_datetime)}
                </p>
              </div>
            </div>

            {/* Vehicle & Passengers */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div className="flex gap-3">
                <Car className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Vehicle</p>
                  <p className="text-white capitalize">{ride.preferred_vehicle_type || 'Sedan'}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-400">Passengers</p>
                  <p className="text-white">{ride.passenger_count || 1}</p>
                </div>
              </div>
            </div>

            {/* Fare */}
            {ride.estimated_fare && (
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Estimated Fare</p>
                  <p className="text-2xl font-bold text-green-400">${ride.estimated_fare?.toFixed(2)}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* What's Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4">What's Next?</h2>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#8B5CF6]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#A78BFA] text-sm font-bold">1</span>
              <span>We'll review your ride request and assign a driver</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#8B5CF6]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#A78BFA] text-sm font-bold">2</span>
              <span>You'll receive a notification when your driver is on the way</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#8B5CF6]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#A78BFA] text-sm font-bold">3</span>
              <span>Track your driver in real-time from your dashboard</span>
            </li>
          </ul>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col gap-3"
        >
          {/* Live Track Button - prominent when driver is assigned */}
          {ride && !ride.isBasicView && ['driver_assigned', 'en_route_pickup', 'arrived_pickup', 'in_progress'].includes(ride.status) && (
            <Button 
              onClick={() => setShowLiveTracker(true)}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-4"
              data-testid="track-driver-live-btn"
            >
              <Navigation className="w-5 h-5 mr-2" />
              Track Driver Live on Map
            </Button>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={`/track?ref=${ride?.reference_number || reference}`} className="flex-1">
              <Button className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white">
                <ExternalLink className="w-4 h-4 mr-2" />
                Track Your Ride
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button className="w-full bg-[#111111] hover:bg-[#1a1a1a] text-white border border-white/10">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 pt-8 border-t border-white/10"
        >
          <p className="text-gray-500 text-sm mb-2">Need help with your booking?</p>
          <a 
            href="mailto:nchelphub@outlook.com" 
            className="text-[#A78BFA] hover:text-[#8B5CF6] text-sm font-medium"
          >
            Contact Support
          </a>
        </motion.div>
      </div>

      {/* Live Ride Tracker Modal */}
      {showLiveTracker && ride && !ride.isBasicView && (
        <LiveRideTracker
          ride={ride}
          onClose={() => setShowLiveTracker(false)}
        />
      )}
    </div>
  );
};

export default RideConfirmation;
